/**
 * CR-5275: TOTP MFA verification step.
 *
 * Shown after a successful password step when the user already has a
 * confirmed TOTPDevice on file. The backend returned `mfa_required: true`
 * + a short-lived session token; we collect the 6-digit code from the
 * user's authenticator app and exchange the session token + code for
 * the real DRF auth token.
 */
import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { getSafeRedirectTarget } from '../lib/authRedirect';

const MfaVerify: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { finalizeMfaLogin } = useAuth();
  const { branding } = useBranding();

  const sessionToken = (location.state as { sessionToken?: string } | null)?.sessionToken;
  const [code, setCode] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  if (!sessionToken) {
    navigate('/login', { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await authAPI.loginVerifyMfa(sessionToken, code.trim());
      const { user, token } = res.data.data as { user: import('../lib/api').User; token: string };
      finalizeMfaLogin(user, token);
      // CR-5887: honour a deep-link carried through from the password step
      // (Daily Quality Summary email links) instead of always /dashboard.
      navigate(getSafeRedirectTarget(location.state) ?? '/dashboard', { replace: true });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail || 'Could not verify code. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      // CR-6244: part of sign-in, which keeps its light card in dark mode.
      className="min-h-screen flex items-center justify-center auth-light"
      style={{ backgroundColor: branding.backgroundColor }}
    >
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Enter your verification code</h1>
            <p className="text-gray-600 mt-2 text-sm">
              Open your authenticator app and enter the 6-digit code shown for Helix.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-center tracking-widest text-lg"
              placeholder="000000"
              disabled={submitting}
              required
              autoFocus
            />
            <button
              type="submit"
              disabled={submitting || code.length !== 6}
              className="w-full py-3 px-4 rounded-lg text-white font-medium disabled:cursor-not-allowed disabled:opacity-60"
              style={{ backgroundColor: branding.buttonColor }}
            >
              {submitting ? 'Verifying…' : 'Verify and sign in'}
            </button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/login" className="text-sm text-indigo-600 hover:text-indigo-500">
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MfaVerify;
